import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import type { CatalogueBundledMemberV2 } from 'models/purchaseFlowRevamp'
import { ProductKindIcon } from '../../steps/catalogue/ProductKindIcon.component'

interface Props {
  /** The pack's members. Empty for any product that is not a pack. */
  members: CatalogueBundledMemberV2[]
}

/**
 * What a pack contains, listed inside the product drawer.
 *
 * Informational only, and deliberately so on both counts. **Nothing is
 * selectable:** a bundle's members are mandatory — the client orders the pack
 * and the platform expands it (`AssignmentCreator.unbundleProducts`) — so a
 * control here would offer a composition the order cannot express. **No member
 * carries a price:** the wire sends none, because the pack's own price is the
 * price, and a figure beside a member would read as an addition to it.
 *
 * Add-ons are not listed per member. The wire's member entries carry no
 * relations of their own; what a pack can be extended with arrives as the
 * pack's OWN `addons`/`extras`, which the drawer's existing pickers render
 * below this section.
 */
export const BundleContentsSection: React.FC<Props> = ({ members }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  if (members.length === 0) return null

  return (
    <Stack gap={0.8} data-cy="pf-revamp-bundle-contents">

      <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
        {t('purchase_flow_revamp:drawer.bundle_includes_label')}
      </Typography>

      <Stack gap={0.6}>
        {members.map((member) => (
          <Stack
            key={member.productCode}
            direction="row"
            alignItems="center"
            gap={0.8}
            sx={{ color: GRAY_700 }}
          >

            <ProductKindIcon kind={member.kind} size="1.6rem" />

            <Typography variant="text-sm">
              {member.displayName}
            </Typography>

          </Stack>
        ))}
      </Stack>

    </Stack>
  )
}
